/**
 * EditNoteModal Component
 *
 * Modal form for editing a list item's note.
 * Pre-populates the text field with the current note value.
 */

import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { Text } from '@/components/ui/text';
import { TextInput } from '@/components/ui/text-input';
import Ionicons from '@expo/vector-icons/Ionicons';
import React, { useEffect, useState } from 'react';
import { Pressable, View } from 'react-native';
import { useUpdateListItemNote } from '../hooks/use-list-item-mutations';
import type { ListItem } from '../types';

interface EditNoteModalProps {
  visible: boolean;
  item: ListItem | null;
  onClose: () => void;
}

export function EditNoteModal({ visible, item, onClose }: EditNoteModalProps) {
  const [note, setNote] = useState('');
  const updateNote = useUpdateListItemNote();

  useEffect(() => {
    if (visible && item) {
      setNote(item.note ?? '');
    }
  }, [visible, item]);

  const handleSave = () => {
    if (!item) return;

    updateNote.mutate(
      {
        listId: item.listId,
        itemId: item.id,
        data: { note: note.trim() || undefined },
      },
      { onSuccess: () => onClose() }
    );
  };

  return (
    <Modal visible={visible} onClose={onClose}>
      <View className="bg-background rounded-xl">
        <Modal.Header>
          <View className="flex-row items-center justify-between">
            <Text className="text-lg font-bold text-foreground">Edit Note</Text>
            <Pressable
              onPress={onClose}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              accessibilityRole="button"
              accessibilityLabel="Close"
            >
              <Ionicons name="close" size={24} className="text-muted-foreground" />
            </Pressable>
          </View>
        </Modal.Header>

        <Modal.Content>
          <TextInput
            label="Note"
            placeholder="Add a note about this item..."
            value={note}
            onChangeText={setNote}
            maxLength={500}
            multiline
            multilineMinHeight={80}
            autoFocus
          />
        </Modal.Content>

        <Modal.Footer>
          <Button variant="ghost" onPress={onClose}>
            Cancel
          </Button>
          <Button onPress={handleSave} loading={updateNote.isPending}>
            Save
          </Button>
        </Modal.Footer>
      </View>
    </Modal>
  );
}
